import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { analyticsApi } from '../services/analyticsApi';
import { LinkedAgent } from '../types';
import { useAuth } from '../context/AuthContext';

// Query keys
export const analyticsKeys = {
  all: ['analytics'] as const,
  analytics: (userId: string, agentIds: string) => ['analytics', { userId, agentIds }] as const,
  linkedAgents: (userId: string) => ['linkedAgents', userId] as const,
};

/**
 * Hook for fetching linked agents
 */
export const useLinkedAgents = () => {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: analyticsKeys.linkedAgents(user?.id || ''),
    queryFn: analyticsApi.getLinkedAgents,
    enabled: !!user?.id,
    staleTime: 0, // Always refetch to ensure fresh data
  });
};

/**
 * Hook for fetching analytics with dependent query pattern
 */
export const useAnalytics = () => {
  const { user } = useAuth();
  const { data: linkedAgents } = useLinkedAgents();
  
  const agentIds = (linkedAgents ?? []).map(agent => agent.id);
  const agentIdsKey = agentIds.join(',');
  
  return useQuery({
    queryKey: analyticsKeys.analytics(user?.id || '', agentIdsKey),
    queryFn: () => analyticsApi.getAnalytics(agentIds.length > 0 ? agentIds : undefined),
    enabled: !!user?.id && Array.isArray(linkedAgents), // Only run when linkedAgents is loaded
    staleTime: 0, // Always refetch to ensure fresh data
  });
};

/**
 * Hook for unlinking agents with optimistic updates
 */
export const useUnlinkAgent = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  return useMutation({
    mutationFn: ({ id, type }: { id: string; type: 'assistant' | 'workflow' }) =>
      analyticsApi.unlinkAgent(id, type),
    
    onMutate: async ({ id, type }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: analyticsKeys.linkedAgents(user?.id || '') });
      
      // Snapshot the previous value
      const previousLinkedAgents = queryClient.getQueryData<LinkedAgent[]>(
        analyticsKeys.linkedAgents(user?.id || '')
      );
      
      // Optimistically remove the agent
      if (previousLinkedAgents) {
        const updatedAgents = previousLinkedAgents.filter(agent => agent.id !== id);
        queryClient.setQueryData(analyticsKeys.linkedAgents(user?.id || ''), updatedAgents);
      }
      
      return { previousLinkedAgents };
    },
    
    onError: (err, variables, context) => {
      // Rollback on error
      if (context?.previousLinkedAgents) {
        queryClient.setQueryData(
          analyticsKeys.linkedAgents(user?.id || ''),
          context.previousLinkedAgents
        );
      }
    },
    
    onSettled: () => {
      // Always refetch after mutation
      queryClient.invalidateQueries({ queryKey: analyticsKeys.linkedAgents(user?.id || '') });
      queryClient.invalidateQueries({ queryKey: analyticsKeys.all });
    },
  });
};

/**
 * Hook to manually refetch analytics data
 */
export const useRefreshAnalytics = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  
  return () => {
    queryClient.invalidateQueries({ queryKey: analyticsKeys.linkedAgents(user?.id || '') });
    queryClient.invalidateQueries({ queryKey: analyticsKeys.all });
  };
};